import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';
import { ZoomLevel } from '~/routes/calendar._index';
import type { CalendarEvent } from './hooks/usePlannerEvents';

/**
 * This class is responsible for calculating the vertical position of each event
 * on the calendar grid. It does this by iterating over each event and checking
 * for any overlapping events. If an event overlaps with another event, it will
 * increment the vertical position of the event until it finds an available slot.
 *
 * The vertical positions of the events will be calculated when the class is instantiated.
 *
 * The vertical positions can be accessed by calling the `getEventVpos` method.
 *
 * OPTIMIZATION: Instead of checking each vpos level with a while loop (O(n²) worst case),
 * we track the "next available vpos" for each cell. This assumes mostly sequential occupation
 * patterns (which is typical for chronologically-processed calendar events).
 *
 * For sparse occupation patterns (e.g., vpos 0, 2, 5 with gaps at 1, 3, 4), the algorithm
 * will still find the first available gap, but may require backtracking in the verification
 * loop. This trade-off favors simplicity and memory efficiency over perfect O(1) gap lookup.
 */
export class CalendarEventOrganiser {
  /** Calculate the number of whole days which an event will span */
  static getCalendarEventLength({
    start,
    end,
  }: {
    start: string;
    end?: string;
  }) {
    const startDate = startOfDay(new Date(start));
    const endDate = startOfDay(addDays(new Date(end || start), 1));
    const days = differenceInCalendarDays(endDate, startDate);
    return days > 0 ? days : 1;
  }

  private events: CalendarEvent[];
  /** occupied[siteIndex]?.[columnIndex] == [undefined, undefined, true] */
  private occupied: (boolean | undefined)[][][];
  /**
   * Tracks the next available vpos for each cell - optimization to avoid O(n) lookups
   * nextAvailable[siteIndex]?.[columnIndex] = next free vpos slot
   */
  private nextAvailable: number[][];
  private zoomLevel: ZoomLevel;
  private vpos: Record<string, number>;

  constructor({
    events,
    zoomLevel,
  }: {
    events: CalendarEvent[];
    zoomLevel: ZoomLevel;
  }) {
    this.events = events;
    this.occupied = [];
    this.nextAvailable = [];
    this.zoomLevel = zoomLevel;
    this.vpos = this.calculateEventVpos();
  }

  private occupyCell = (
    siteIndex: number,
    columnIndex: number,
    vpos: number
  ) => {
    if (this.occupied[siteIndex] == undefined) {
      this.occupied[siteIndex] = [];
      this.nextAvailable[siteIndex] = [];
    }
    if (this.occupied[siteIndex][columnIndex] == undefined) {
      this.occupied[siteIndex][columnIndex] = [];
    }
    this.occupied[siteIndex][columnIndex][vpos] = true;

    // Update nextAvailable - if we just occupied vpos, next available is at least vpos + 1
    // TRADE-OFF: This only updates when vpos >= currentNext, which optimizes for sequential
    // occupation patterns. For sparse patterns (e.g., occupying vpos 0, then 2, skipping 1),
    // nextAvailable will point past the gap, requiring the verification loop in
    // getNextAvailableVpos to backtrack. This is acceptable because:
    // 1. Calendar events are typically processed chronologically, leading to sequential occupation
    // 2. The verification loop is still faster than checking every vpos level
    // 3. Tracking all gaps would require additional memory (e.g., a Set per cell)
    const currentNext = this.nextAvailable[siteIndex][columnIndex] ?? 0;
    if (vpos >= currentNext) {
      this.nextAvailable[siteIndex][columnIndex] = vpos + 1;
    }
  };

  private occupyCellRange = (
    siteIndex: number,
    startIndex: number,
    endIndex: number,
    vpos: number
  ) => {
    for (let i = startIndex; i <= endIndex; i++) {
      this.occupyCell(siteIndex, i, vpos);
    }
  };

  private isCellOccupied = (
    siteIndex: number,
    columnIndex: number,
    vpos: number
  ) => this.occupied[siteIndex]?.[columnIndex]?.[vpos] || false;

  /**
   * Get the next available vpos for a range of columns
   * OPTIMIZATION: Uses nextAvailable array for O(range) lookup instead of O(range * vposLevels)
   *
   * PERFORMANCE NOTE: The verification loop below may run more than 0-1 times if cells are
   * occupied non-sequentially (sparse patterns). For example:
   * - Occupy vpos 0 in column 1 → nextAvailable[1] = 1
   * - Occupy vpos 2 in column 1 → nextAvailable[1] = 3 (gap at vpos 1)
   * - Next query will start at candidateVpos=3, then backtrack through the loop to find vpos 1
   *
   * This is acceptable because:
   * - Calendar events are typically processed chronologically, producing mostly sequential patterns
   * - Even with sparse patterns, this is still O(gaps) which is typically << O(vposLevels)
   * - Alternative solutions (tracking gaps in Sets) add memory overhead and complexity
   *
   * If profiling shows this is a bottleneck for your use case, consider tracking occupied
   * gaps explicitly per cell using Set<number> to achieve true O(1) gap lookup.
   */
  private getNextAvailableVpos = (
    siteIndex: number,
    startIndex: number,
    endIndex: number
  ) => {
    // Find the maximum "next available" across all cells in the range
    // This gives us a good starting candidate
    let candidateVpos = 0;
    for (let i = startIndex; i <= endIndex; i++) {
      const cellNextAvailable = this.nextAvailable[siteIndex]?.[i] ?? 0;
      if (cellNextAvailable > candidateVpos) {
        candidateVpos = cellNextAvailable;
      }
    }

    // Verify this candidate is actually available (handles sparse occupation)
    // For sequential patterns: runs 0-1 times
    // For sparse patterns: may run multiple times to backtrack through gaps
    while (
      this.isCellRangeOccupied(siteIndex, startIndex, endIndex, candidateVpos)
    ) {
      candidateVpos++;
    }

    return candidateVpos;
  };

  private isCellRangeOccupied = (
    siteIndex: number,
    startIndex: number,
    endIndex: number,
    vpos: number
  ) => {
    for (let i = startIndex; i <= endIndex; i++) {
      if (this.isCellOccupied(siteIndex, i, vpos)) {
        return true;
      }
    }
    return false;
  };

  /** Calculate the vertical position of each event */
  private calculateEventVpos = () => {
    const eventVpos: Record<string, number> = {};

    this.events.forEach(
      ({ x: columnIndex, y: siteIndex, event, compositeKey }) => {
        // Not relevant for bookings
        if (event.status == 'Booking') return;

        const days = CalendarEventOrganiser.getCalendarEventLength(event);

        const vpos = this.getNextAvailableVpos(
          siteIndex,
          columnIndex,
          columnIndex + days - 1
        );

        const key = compositeKey || event.id;
        eventVpos[key] = vpos;

        this.occupyCellRange(
          siteIndex,
          columnIndex,
          columnIndex + days - 1,
          vpos
        );
      }
    );

    return eventVpos;
  };

  getEventVpos = (): Record<string, number> => {
    return this.vpos;
  };

  getOccupied = () => this.occupied;
}
